// ============================================================
// LHS PRODUCTION HELPDESK VIEW
// Backend-controlled state machine, single action runner
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RefreshCw, Search, Filter, ChevronRight, MapPin,
  Clock, AlertTriangle, Phone, MessageSquare, MoreVertical,
  CheckCircle, XCircle, FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// Components
import CurrentMission from '../../components/CurrentMission';
import AssignContractorModal from '../../components/AssignContractorModal';
import QuoteApprovalModal from '../../components/QuoteApprovalModal';
import InvoiceSubmitModal from '../../components/InvoiceSubmitModal';

// Utils
import { 
  runJobAction, 
  dedupeJobs, 
  LHS_API, 
  getStatusColor, 
  getPriorityColor,
  isTerminalState,
  canCancel
} from '../../utils/lhsCore';

// ============================================================
// CONSTANTS
// ============================================================

const VIEW_FILTERS = [
  { key: 'active', label: 'Active', color: 'bg-blue-500' },
  { key: 'completed', label: 'Completed', color: 'bg-green-500' },
  { key: 'closed', label: 'Closed', color: 'bg-gray-500' },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-red-500' },
  { key: 'all', label: 'All Jobs', color: 'bg-purple-500' },
];

// ============================================================
// JOB CARD COMPONENT
// ============================================================

function JobCard({ job, isSelected, onClick }) {
  const statusColor = getStatusColor(job.status);
  const priorityColor = getPriorityColor(job.priority);
  const nextAction = job.next_action?.label || 'Review';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ scale: 1.01 }}
      onClick={onClick}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
        isSelected 
          ? 'border-[#174C86] bg-blue-50/50 shadow-lg' 
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
      }`}
      data-testid={`job-card-${job.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[#0F172A]" data-testid="job-ref">
            {job.job_ref}
          </span>
          {job.po_number && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
              {job.po_number}
            </span>
          )}
        </div>
        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${priorityColor.bg} ${priorityColor.text}`}>
          {job.priority?.toUpperCase()}
        </span>
      </div>

      {/* Location */}
      <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-2">
        <MapPin className="w-4 h-4" />
        <span className="truncate">{job.park_name} {job.plot && `- Plot ${job.plot}`}</span>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 line-clamp-2 mb-3">
        {job.description}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className={`px-2.5 py-1 text-xs font-medium rounded-lg ${statusColor.bg} ${statusColor.text}`}>
          {job.status}
        </span>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="w-3.5 h-3.5" />
          <span>{nextAction}</span>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// JOB DETAIL PANEL
// ============================================================

function JobDetailPanel({ 
  job, 
  onAssignContractors,
  onApproveQuote,
  onSubmitInvoice,
  onJobUpdated,
  onCancel,
  loading 
}) {
  if (!job) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Select a job to view details</p>
        </div>
      </div>
    );
  }

  const statusColor = getStatusColor(job.status);
  const priorityColor = getPriorityColor(job.priority);
  const assignments = job.contractor_assignments || [];
  const quotes = job.quotes || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const handleCancel = async () => {
    const reason = window.prompt('Enter cancellation reason:');
    if (!reason) return;

    try {
      const updatedJob = await runJobAction(
        LHS_API.cancelJob(job.id),
        { reason }
      );
      onJobUpdated(updatedJob);
      toast.success('Job cancelled');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-5 border-b bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-[#0F172A]">{job.job_ref}</span>
            {job.po_number && (
              <span className="px-3 py-1 text-sm font-medium rounded-lg bg-emerald-100 text-emerald-700">
                PO: {job.po_number}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 text-sm font-semibold rounded-lg ${priorityColor.bg} ${priorityColor.text}`}>
              {job.priority?.toUpperCase()}
            </span>
            <span className={`px-3 py-1.5 text-sm font-medium rounded-lg ${statusColor.bg} ${statusColor.text}`}>
              {job.status}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="w-4 h-4" />
          <span>{job.park_name} {job.plot && `- Plot ${job.plot}`}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Current Mission */}
        <CurrentMission
          job={job}
          onAssignContractors={onAssignContractors}
          onApproveQuote={onApproveQuote}
          onSubmitInvoice={onSubmitInvoice}
          onJobUpdated={onJobUpdated}
          onError={(msg) => toast.error(msg)}
          loading={loading}
        />

        {/* Description */}
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Description
          </h3>
          <p className="text-gray-700">{job.description}</p>
        </div>

        {/* Contractor Assignments */}
        {assignments.length > 0 && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Contractors ({assignments.length})
            </h3>
            <div className="space-y-3">
              {assignments.map((assignment) => (
                <div 
                  key={assignment.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {assignment.contractor_name || assignment.contractor_id}
                    </div>
                    <div className="text-sm text-gray-500">
                      {assignment.status}
                    </div>
                  </div>
                  {assignment.quote && (
                    <div className="text-right">
                      <div className="font-bold text-gray-900">
                        {formatCurrency(assignment.quote.amount)}
                      </div>
                      <div className={`text-xs ${
                        assignment.quote.status === 'approved' ? 'text-green-600' :
                        assignment.quote.status === 'rejected' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {assignment.quote.status}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Financial Summary */}
        {job.approved_amount && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Financial Summary
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Approved Amount</div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(job.approved_amount)}
                </div>
              </div>
              {job.invoice && (
                <div>
                  <div className="text-sm text-gray-500">Invoice Amount</div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(job.invoice.amount)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cancel Button */}
        {canCancel(job) && (
          <button
            onClick={handleCancel}
            className="w-full py-3 rounded-xl border-2 border-red-200 text-red-600 
                       hover:bg-red-50 hover:border-red-300 font-medium transition-all
                       flex items-center justify-center gap-2"
            data-testid="cancel-job-btn"
          >
            <XCircle className="w-5 h-5" />
            Cancel Job
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN HELPDESK VIEW
// ============================================================

export default function LHSHelpdeskView() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewFilter, setViewFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);

  // Modals
  const [assignModalJob, setAssignModalJob] = useState(null);
  const [quoteModalJob, setQuoteModalJob] = useState(null);
  const [invoiceModalJob, setInvoiceModalJob] = useState(null);

  // Load jobs
  const loadJobs = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await fetch(LHS_API.listJobs(viewFilter, 200));
      if (res.ok) {
        const data = await res.json();
        const dedupedJobs = dedupeJobs(data.jobs || []);
        setJobs(dedupedJobs);

        // Update selected job if it exists in new data
        if (selectedJob) {
          const updated = dedupedJobs.find(j => j.id === selectedJob.id);
          if (updated) setSelectedJob(updated);
        }
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [viewFilter, selectedJob]);

  // Load metrics
  const loadMetrics = useCallback(async () => {
    try {
      const res = await fetch(LHS_API.metrics());
      if (res.ok) {
        const data = await res.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Failed to load metrics:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadJobs();
    loadMetrics();
  }, [viewFilter]);

  // Filter jobs by search
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.job_ref?.toLowerCase().includes(query) ||
      job.park_name?.toLowerCase().includes(query) ||
      job.description?.toLowerCase().includes(query) ||
      job.po_number?.toLowerCase().includes(query)
    );
  });

  // Handle job update from action
  const handleJobUpdated = (updatedJob) => {
    setJobs(prev => {
      const newJobs = prev.map(j => j.id === updatedJob.id ? updatedJob : j);
      return dedupeJobs(newJobs);
    });
    setSelectedJob(updatedJob);
    loadMetrics();
    toast.success('Job updated');
  };

  // Refresh
  const handleRefresh = () => {
    loadJobs(false);
    loadMetrics();
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Helpdesk</h1>
            <p className="text-sm text-gray-500">
              {metrics ? `${metrics.active_jobs} active jobs` : 'Loading...'}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* View Filters */}
        <div className="flex items-center gap-2 mb-4">
          {VIEW_FILTERS.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setViewFilter(filter.key)}
              data-testid={`filter-${filter.key}`}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                viewFilter === filter.key
                  ? `${filter.color} text-white shadow-md`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
              {metrics && filter.key !== 'all' && (
                <span className="ml-1.5 opacity-80">
                  ({metrics[`${filter.key}_jobs`] || 0})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by job ref, park, description..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Job List */}
        <div className="w-[420px] border-r bg-white overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No jobs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {filteredJobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJob?.id === job.id}
                    onClick={() => setSelectedJob(job)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="flex-1 bg-gray-50">
          <JobDetailPanel
            job={selectedJob}
            onAssignContractors={(job) => setAssignModalJob(job)}
            onApproveQuote={(job) => setQuoteModalJob(job)}
            onSubmitInvoice={(job) => setInvoiceModalJob(job)}
            onJobUpdated={handleJobUpdated}
            loading={loading}
          />
        </div>
      </div>

      {/* Modals */}
      <AssignContractorModal
        job={assignModalJob}
        isOpen={!!assignModalJob}
        onClose={() => setAssignModalJob(null)}
        onJobUpdated={(job) => {
          handleJobUpdated(job);
          setAssignModalJob(null);
        }}
        onError={(msg) => toast.error(msg)}
      />

      <QuoteApprovalModal
        job={quoteModalJob}
        isOpen={!!quoteModalJob}
        onClose={() => setQuoteModalJob(null)}
        onJobUpdated={(job) => {
          handleJobUpdated(job);
          setQuoteModalJob(null);
        }}
        onError={(msg) => toast.error(msg)}
      />

      <InvoiceSubmitModal
        job={invoiceModalJob}
        isOpen={!!invoiceModalJob}
        onClose={() => setInvoiceModalJob(null)}
        onJobUpdated={(job) => {
          handleJobUpdated(job);
          setInvoiceModalJob(null);
        }}
        onError={(msg) => toast.error(msg)}
      />
    </div>
  );
}
